import React from "react";
import { LoaderFunctionArgs, MetaFunction, redirect } from "react-router";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { gql } from "@apollo/client";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import {
  ensureFullyPublicOrJoinedMindspace,
  getAccessToken,
} from "~/utils/ensure-authenticated.server";
import { getServerClient } from "~/graphql";
import {
  MindspaceEventQuery,
  MindspaceEventQueryVariables,
  ProfileByUsernameDocument,
  SpeakerFragment,
  MindspaceEventDocument,
} from "~/graphql/codegen/graphql";
import { useLoaderData, useRouteLoaderData } from "react-router";
import { deriveAccessSnapshot } from "~/routes/_app.events_.$slug_.live/use-live-event-access";
import {
  LiveEventCTAButton,
  type LiveEventCTAState,
} from "~/components/events/live-event-cta-banner";
// import { getCurrentSpaceUniqueName } from "~/utils/current-space-cookie.server";
import PageContainer from "~/components/containers/page-container";
import { Card, CardContent, CardHeader, CardTitle } from "@mindstonehq/ui";
import MindspaceEventDetails from "~/components/events/mindspace-event-details";
import Markdown from "marked-react";
import MindspaceEventAgenda from "~/components/events/mindspace-event-agenda";
import MindspaceEventSpeakers from "~/components/events/mindspace-event-speakers";
import MindspaceEventSponsors from "~/components/events/mindspace-event-sponsors";
// import MindspaceEventAttendees from "~/components/mindspace-events/mindspace-event-attendees";
import MindspaceEventRSVP from "~/components/events/mindspace-event-rsvp";
import MindspaceEventMap from "~/components/events/mindspace-event-map";
import GetInvolvedHub from "~/components/community/get-involved-hub";
import EventCommunityTeam from "~/components/community/event-community-team";
import {
  enrichCommunityTeam,
  type CommunityTeam,
  type ResolvedCommunityTeam,
  type ResolvedProfile,
} from "~/components/community/enrich-community-team";
import { communityAdminFetch } from "~/lib/community-admin-client.server";
import { PageColumn } from "~/components/containers/page-column";
import { currentSpaceUniqueName } from "~/hooks/check-authenticated";
import MindspaceEventAttendees from "~/components/events/mindspace-event-attendees";
import type { loader as RootLoader } from "~/root";
import usePageView from "~/hooks/page-view";
import { graphql } from "~/graphql/codegen";

const SPEAKER_FRAGMENT = graphql(`
  fragment Speaker on Profile {
    id
    displayName
    username
    profilePictureURL
    tagLine
    mine
    following
  }
`);

const MINDSPACE_EVENT_AGENDA_FRAGMENT = graphql(`
  fragment MindspaceEventAgenda on AgendaItem {
    title
    description
    speaker {
      ...Speaker
    }
    speakerName
    startDatetime
    endDatetime
    recording {
      id
    }
  }
`);

const MINDSPACE_EVENT_FRAGMENT = graphql(`
  fragment MindspaceEvent on MindspaceEvent {
    id
    slug
    name
    publicUrl
    startDatetime
    endDatetime
    locationShort
    backgroundImage {
      medium
    }
    location {
      ... on OnlineLocation {
        text
        url
        joinUrl
      }
      ... on GeoLocation {
        address
      }
    }
    eventDescription
    state
    price {
      amountMinor
      currency
      display
      isPaid
    }
    attendance {
      capacity
      available
      registered
      attendees(size: $attendeesSize, offset: $attendeesOffset) {
        hasMore
        nextItem
        total
        profiles {
          id
          displayName
          profilePictureURL
        }
      }
    }
    attending
    requiresApproval
    approvalStatus
    agenda {
      ...MindspaceEventAgenda
    }
    sponsors {
      sponsor {
        id
        name
        description
        logourl
        url
      }
      isPartner
    }
    redirectURL
  }
`);

const MINDSPACE_EVENT_QUERY = graphql(`
  query MindspaceEvent(
    $uniqueName: String!
    $identifier: String!
    $attendeesSize: Int
    $attendeesOffset: Int
  ) {
    mindspace(identifier: $uniqueName) {
      name
      uniqueName
      modules {
        events
      }
      events {
        event(identifier: $identifier) {
          ...MindspaceEvent
        }
      }
      currentUserPermission {
        role
        isChampion
        isCoach
      }
      configuration {
        public
        inviteOnly
      }
    }
  }
`);

// Registers the query for codegen; intentionally unused at runtime.
const _PROFILE_BY_USERNAME = graphql(`
  query ProfileByUsername($username: String!) {
    profile(username: $username) {
      id
      username
      displayName
      profilePictureURL
      tagLine
    }
  }
`);

interface MindspaceEventAttendedQuery {
  mindspace: {
    events: {
      event: {
        id: string;
        attended?: boolean | null;
        cohortId?: string | null;
      } | null;
    };
  };
}

interface MindspaceEventAttendedQueryVariables {
  uniqueName: string;
  identifier: string;
}

const MINDSPACE_EVENT_ATTENDED_QUERY = gql`
  query MindspaceEventAttended($uniqueName: String!, $identifier: String!) {
    mindspace(identifier: $uniqueName) {
      events {
        event(identifier: $identifier) {
          id
          attended
          cohortId
        }
      }
    }
  }
` as TypedDocumentNode<
  MindspaceEventAttendedQuery,
  MindspaceEventAttendedQueryVariables
>;

async function computeLiveEventCta(args: {
  isLoggedIn: boolean;
  attending: boolean;
  requiresApproval: boolean;
  approvalStatus: string | null | undefined;
  startDatetime: string;
  endDatetime: string;
  slug: string;
  uniqueName: string;
  identifier: string;
  accessToken: string | null;
}): Promise<LiveEventCTAState | null> {
  if (!args.isLoggedIn) return null;
  if (!args.attending) return null;

  let attended = false;
  let cohortId: string | null = null;
  try {
    const { data } = await getServerClient().query<
      MindspaceEventAttendedQuery,
      MindspaceEventAttendedQueryVariables
    >({
      query: MINDSPACE_EVENT_ATTENDED_QUERY,
      variables: { uniqueName: args.uniqueName, identifier: args.identifier },
      fetchPolicy: "no-cache",
      context: {
        headers: {
          Authorization: args.accessToken
            ? `Bearer ${args.accessToken}`
            : undefined,
        },
      },
    });
    attended = !!data?.mindspace?.events?.event?.attended;
    cohortId = data?.mindspace?.events?.event?.cohortId ?? null;
  } catch (err) {
    console.error("[LiveEventCTA] Failed to fetch attended status", err);
    // Fail closed: if we can't confirm the event is not a program/cohort event,
    // skip the CTA so we don't surface the meetup live-page funnel on a
    // program event in the event of a transient lookup failure.
    return null;
  }

  if (cohortId) return null;

  const snapshot = deriveAccessSnapshot({
    isLoggedIn: true,
    attending: args.attending,
    attended,
    requiresApproval: args.requiresApproval,
    approvalStatus: args.approvalStatus ?? null,
    startDatetime: new Date(args.startDatetime),
    endDatetime: new Date(args.endDatetime),
    now: new Date(),
  });

  if (
    snapshot.tier === "not_logged_in" ||
    snapshot.tier === "logged_in_no_rsvp"
  ) {
    return null;
  }

  return { tier: snapshot.tier, slug: args.slug };
}

export const meta: MetaFunction<
  typeof loader,
  {
    root: typeof RootLoader;
  }
> = ({ data: loaderData, params, location, matches }) => {
  if (!loaderData || !loaderData.mindspace.events?.event) {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- brand SEO title
    return [{ title: "Mindstone" }];
  }
  const rootData = matches.find((match) => match.id === "root");
  const uniqueName = rootData?.data.currentSpace.uniqueName || "app";
  const event = loaderData.mindspace.events?.event;
  const fallbackTitle = i18n._(msg`Mindstone Event`);
  const fallbackDescription = i18n._(msg`View this event on Mindstone.`);
  const title = event?.name || fallbackTitle;
  const description = (event?.eventDescription || fallbackDescription).substring(0, 160);
  return [
    {
      title,
    },
    {
      name: "description",
      content: description,
    },
    {
      property: "og:site_name",
      // eslint-disable-next-line lingui/no-unlocalized-strings -- brand name
      content: "Mindstone",
    },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    {
      property: "og:image",
      content: event.backgroundImage.medium,
    },
    {
      property: "og:image:url",
      content: event.backgroundImage.medium,
    },
    { property: "og:image:type", content: "image/png" },
    {
      property: "og:url",
      content: `https://app.mindstone.com/${location.pathname}`,
    },
    {
      property: "twitter:card",
      content: "summary_large_image",
    },
    {
      property: "twitter:site",
      // eslint-disable-next-line lingui/no-unlocalized-strings -- twitter handle
      content: "@MindstoneHQ",
    },
    {
      property: "twitter:creator",
      // eslint-disable-next-line lingui/no-unlocalized-strings -- twitter handle
      content: "@MindstoneHQ",
    },
    {
      property: "twitter:title",
      content: title,
    },
    {
      property: "twitter:description",
      content: description,
    },
    {
      property: "twitter:image",
      content: event.backgroundImage.medium,
    },
    {
      property: "twitter:image:src",
      content: event.backgroundImage.medium,
    },
    {
      property: "twitter:cta",
      content: i18n._(msg`View in Mindstone`),
    },
    { property: "twitter:widgets:new-embed-design", content: "on" },
    { property: "twitter:widgets:csp", content: "on" },
    { property: "og:type", content: "profile" },
    {
      property: "og:profile:username",
      content: uniqueName,
    },
  ];
};

type CityDemand = {
  interestCount: number;
  wantedOrganizer: boolean;
  locationName: string | null;
};

type PublishedQuestion = {
  id: number;
  authorName: string;
  authorUsername: string | null;
  questionText: string;
  answerText: string;
  talkId: number | null;
  talkTitle: string | null;
  answeredByName: string | null;
  answeredAt: string | null;
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const accessToken = await getAccessToken(request);
  const uniqueName = currentSpaceUniqueName(request);

  const eventSlug = params["slug"];

  if (!uniqueName || !eventSlug) {
    throw new Response(null, {
      status: 404,
      statusText: "Not Found",
    });
  }

  const { data: eventData, error } = await getServerClient()
    .query({
      query: MindspaceEventDocument,
      fetchPolicy: "no-cache",
      context: {
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        },
      },
      variables: {
        uniqueName,
        identifier: eventSlug,
        attendeesSize: 8,
        attendeesOffset: 0,
      },
    })
    .catch((error) => {
      throw error;
    });
  if (error) {
    throw error;
  }

  if (!eventData?.mindspace.modules.events) {
    ensureFullyPublicOrJoinedMindspace(uniqueName, eventData?.mindspace);
    throw redirect(`/`);
  }

  const event = eventData.mindspace.events?.event;
  const liveEventCta = event
    ? await computeLiveEventCta({
        isLoggedIn: !!accessToken,
        attending: !!event.attending,
        requiresApproval: event.requiresApproval === true,
        approvalStatus: event.approvalStatus,
        startDatetime: event.startDatetime,
        endDatetime: event.endDatetime,
        slug: event.slug,
        uniqueName,
        identifier: eventSlug,
        accessToken: accessToken ? String(accessToken) : null,
      })
    : null;

  const emptyRawTeam: CommunityTeam = { atEvent: [], city: [] };
  const emptyResolvedTeam: ResolvedCommunityTeam = { atEvent: [], city: [] };

  let rawCommunityTeam = emptyRawTeam;
  let cityDemand: CityDemand | null = null;
  let publishedQuestions: PublishedQuestion[] = [];
  if (uniqueName === "community" && event?.slug) {
    try {
      const teamResponse = await communityAdminFetch<
        CommunityTeam & { demand?: CityDemand | null }
      >({
        method: "GET",
        path: `/api/public/events/${encodeURIComponent(event.slug)}/community-team`,
        request,
      });
      rawCommunityTeam = {
        atEvent: teamResponse.atEvent,
        city: teamResponse.city,
      };
      cityDemand = teamResponse.demand ?? null;
    } catch (err) {
      console.error("[EventPage] Failed to fetch community team", err);
      rawCommunityTeam = emptyRawTeam;
    }

    try {
      const qaResponse = await communityAdminFetch<{
        questions?: PublishedQuestion[];
      }>({
        method: "GET",
        path: `/api/public/events/${encodeURIComponent(event.slug)}/published-qa`,
        request,
      });
      publishedQuestions = qaResponse.questions ?? [];
    } catch (err) {
      console.error("[EventPage] Failed to fetch published event Q&A", err);
      publishedQuestions = [];
    }
  }

  let communityTeam = emptyResolvedTeam;
  if (uniqueName === "community" && event?.slug) {
    try {
      communityTeam = await enrichCommunityTeam(rawCommunityTeam, {
        resolveProfile: async (username): Promise<ResolvedProfile | null> => {
          const { data } = await getServerClient().query({
            query: ProfileByUsernameDocument,
            variables: { username },
            fetchPolicy: "no-cache",
            context: {
              headers: {
                Authorization: accessToken
                  ? `Bearer ${accessToken}`
                  : undefined,
              },
            },
          });
          const profile = data?.profile;
          if (!profile) return null;
          return {
            id: profile.id,
            username: profile.username,
            displayName: profile.displayName || profile.username,
            profilePictureURL: profile.profilePictureURL || "",
            tagLine: profile.tagLine ?? null,
          };
        },
      });
    } catch (err) {
      console.error("[EventPage] Failed to enrich community team profiles", err);
      communityTeam = emptyResolvedTeam;
    }
  }

  return {
    ...eventData,
    liveEventCta,
    communityTeam,
    cityDemand,
    publishedQuestions,
  };
}

function EventPublishedQuestions({
  questions,
}: {
  questions: PublishedQuestion[];
}) {
  const { t } = useLingui();
  const fallbackResponder = t(msg`Mindstone team`);

  if (questions.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans>Answered questions</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {questions.map((question) => {
          const answeredBy = question.answeredByName?.trim() || fallbackResponder;
          return (
            <article
              key={question.id}
              className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-4"
            >
              {question.talkTitle && (
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-emerald-700">
                  <Trans>Talk: {question.talkTitle}</Trans>
                </p>
              )}
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    <Trans>Question</Trans>
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-950">
                    {question.questionText}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    <Trans>Asked by</Trans> {question.authorName}
                    {question.authorUsername ? (
                      <span> @{question.authorUsername}</span>
                    ) : null}
                  </p>
                </div>
                <div className="border-l-2 border-emerald-300 pl-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">
                    <Trans>Answer</Trans>
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                    {question.answerText}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    <Trans>Answered by {answeredBy}</Trans>
                  </p>
                </div>
              </div>
            </article>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default function Event() {
  const { t } = useLingui();
  usePageView("Event");
  const loaderData = useLoaderData<typeof loader>();
  const rootData = useRouteLoaderData<typeof RootLoader>("root");
  const event = loaderData.mindspace.events?.event;
  const liveEventCta = loaderData.liveEventCta;
  const communityTeam = loaderData.communityTeam;
  const publishedQuestions = loaderData.publishedQuestions;
  const speakers = event?.agenda
    .map((a) => a.speaker)
    .filter((a): a is SpeakerFragment => !!a)
    .filter((s, i, arr) => arr.findIndex((x) => x.id === s.id) === i) ?? [];
  const attendees = event?.attendance.attendees.profiles;
  if (!event) {
    return (
      <PageContainer title={t(msg`Not found`)}>
        <Trans>Event couldn't be found</Trans>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageContainer.Breadcrumb
        href={"/events"}
        key={"events"}
        title={t(msg`Events`)}
      />
      <PageContainer.Breadcrumb key={event.id} title={event.name} />

      <PageColumn className="col-span-12 md:col-span-8">
        <Card className="py-0">
          <img
            className="w-full rounded-lg object-contain max-h-[500px] h-auto"
            src={event.backgroundImage.medium}
            alt={t(msg`${event.name} header image`)}
          />
          <CardHeader className="overflow-hidden">
            <CardTitle>{event.name}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col space-y-5">
            {liveEventCta && (
              <div className="flex lg:hidden">
                <LiveEventCTAButton
                  state={liveEventCta}
                  className="w-full"
                />
              </div>
            )}
            <div className="flex flex-col space-y-2">
              <div className="flex flex-col lg:hidden">
                <MindspaceEventDetails event={event} />
              </div>
              <div className="prose dark:prose-invert">
                <Markdown>{event.eventDescription}</Markdown>
              </div>
            </div>
            {!!event.agenda.length && (
              <MindspaceEventAgenda agendaItems={event.agenda} />
            )}
            {!!speakers.length && (
              <MindspaceEventSpeakers speakers={speakers} />
            )}
            {!!event.sponsors.length && (
              <MindspaceEventSponsors
                sponsors={event.sponsors}
                eventId={event.id}
                communityAdminUrl={
                  rootData?.config.communityAdminUrl ||
                  rootData?.config.communityUrl ||
                  ""
                }
              />
            )}
            <EventPublishedQuestions questions={publishedQuestions} />
            {!!attendees?.length && (
              <MindspaceEventAttendees attendees={attendees} />
            )}
          </CardContent>
        </Card>
      </PageColumn>
      <PageColumn className="col-span-12 md:col-span-4">
        <MindspaceEventRSVP event={event} liveEventCta={liveEventCta} />
        {event.location?.__typename === "GeoLocation" && (
          <MindspaceEventMap address={event.location.address} />
        )}
        <EventCommunityTeam
          atEvent={communityTeam.atEvent}
          city={communityTeam.city}
          locationName={event.locationShort ?? ""}
        />
        {event.locationShort && (
          <GetInvolvedHub
            locationName={event.locationShort}
            source="event_page"
            interestCount={loaderData.cityDemand?.interestCount}
          />
        )}
      </PageColumn>

      {/*<MindspaceEventRSVPMobile event={event} />*/}
    </PageContainer>
  );
}
